import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { getPhotoPicker, PHOTO_PICKER_MODEL } from '../../../../../components/PhotoPicker/SubComponent/Models/Models';
import routes from '../../../../../Routes/routes';
import { FILE_TYPE_MODEL, FILE_TYPE_SIDE_MODEL } from '../../Visitor/Model/Model';
import { useDispatch, useSelector } from 'react-redux';
import { GetInvitationById, PublicCreateUserFile } from '../../../../../reduxToolkit/Invitation/InvitationApi';
import { PublicUploadImage } from '../../../../../reduxToolkit/ShareSlice/shareApi';
import { accessMethodModel } from '../../../../../enums/accessMethodEnum';
import { PublicCreateUserImage } from '../../../../../reduxToolkit/CompanyEmployees/CompanyEmployeesApi';
import {getUrlParameters, STEP_MODEL, TAB_INDEX} from '../Model/Model';
import {TABLES} from '../../../../../Apis/Tables';
import {STATUS_MODEL} from "../../../../../enums/statusEnum";

/**
 *
 * @Author: Cecilia López Martínez
 * @JobTitle : Full Stack Developer - IBL
 * @Email : clopez@ibl.mx
 *
 */
const CreateValidationApp = () => {

    const { type, documentType, id, guardFlowId } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { getInvitationById } = useSelector((state) => state.InvitationSlice);

    const [steps, setSteps] = useState(null);
    const [activeStep, setActiveStep] = useState(1);

    const [passport, setPassport] = useState({ ...PHOTO_PICKER_MODEL })
    const [idCardBack, setIdCardBack] = useState({ ...PHOTO_PICKER_MODEL })
    const [idCardFront, setIdCardFront] = useState({ ...PHOTO_PICKER_MODEL })
    const [facePhoto, setFacePhoto] = useState({ ...PHOTO_PICKER_MODEL })

    const [nextPhoto, setNextPhoto] = useState(FILE_TYPE_SIDE_MODEL.OBVERSE.id);
    const [startup, setStartup] = useState(true); // To restart the camera
    const [reopenSuggestionShow, setReopenSuggestionShow] = useState(true); // To control the suggestions modal
    const [loading, setLoading] = useState(false);
    const [userId, setUserId] = useState(false);

    const [btnDisabled, setBtnDisabled] = useState(false);
    const [showModalCancelPicker, setShowModalCancelPicker] = useState(false);
    const invitationId = getInvitationById?.invitation?.id;

    /**
     * retrieves data from the invitation
     */
    useEffect(() => {
        if (id) {
            if(id !== invitationId){
                dispatch(GetInvitationById(id)).then(({payload:{data:{data,success}}})=>{
                    if (success) goBack(data?.invitation?.identityValidation)
                });
            }else{
                goBack(getInvitationById?.invitation?.identityValidation)
            }
        }
    }, [id, invitationId, dispatch]);

    const goBack = (identityValidation) => {
        if(identityValidation !== STATUS_MODEL.PENDING_TO_UPLOAD_INFORMATION){
            navigate(getUrlParameters({ url: routes.invitationDetails.registrationProcess, id: id, guardFlowId }));
        }
    }

    useEffect(() => {
        if(activeStep == TAB_INDEX.TWO || activeStep == TAB_INDEX.FIVE){
            if(!startup){
                setStartup(true);
            }
        }
    }, [activeStep]);

    /**
     * Get the userId and assign it to a state
     */
    useEffect(() => {
        if (getInvitationById && getInvitationById?.invitation?.visitorId) {
            setUserId(getInvitationById.invitation.visitorId)
        }
    }, [getInvitationById]);

    /**
     * Control of the total steps and the number of steps to be displayed
     */
    useLayoutEffect(() => {
        if (getInvitationById?.registeredFiles <= 0 && getInvitationById && getInvitationById?.biometricVisitor) {
            setSteps({ ...STEP_MODEL.COMPLETE })
        } else if (getInvitationById?.registeredFiles >= 1 && getInvitationById && getInvitationById?.biometricVisitor) {
            setSteps({ ...STEP_MODEL.FACE })
        } else if (getInvitationById && !getInvitationById?.biometricVisitor) {
            if(getInvitationById?.registeredFiles >= 1){//If user reload page, return registration process view
                navigate(getUrlParameters({url: routes.invitationDetails.registrationProcess, id, guardFlowId}));
                return;
            }
            setSteps({ ...STEP_MODEL.FILES });
        }
    }, [getInvitationById, activeStep]);

    /**
     * Effect to control the “Next” button's disabled state
     */
    useEffect(() => {
        const value = steps?.NAME;
        switch (value) {
            case STEP_MODEL.COMPLETE.NAME:
                btnDisabledCompleteSteps();
                break;
            case STEP_MODEL.FACE.NAME:
                btnDisabledFaceSteps();
                break;
            case STEP_MODEL.FILES.NAME:
                btnDisabledFilesSteps();
                break;
            default:
                break;
        }
    }, [activeStep, passport, idCardBack, idCardFront, facePhoto, steps]);

    /**
     * Function for the "Next" button of the INE/Passport files.
     */
    const validateBtnFiles = () => {
        setBtnDisabled(true);
        if (type == FILE_TYPE_MODEL.PASSPORT.id) {
            if (passport.selfieSrc != null) {
                setBtnDisabled(false);
            }
        } else if (type == FILE_TYPE_MODEL.INE.id) {
            setBtnDisabled(true);
            if (nextPhoto == FILE_TYPE_SIDE_MODEL.OBVERSE.id && idCardFront.selfieSrc != null) {
                setBtnDisabled(false);
            } else if (nextPhoto == FILE_TYPE_SIDE_MODEL.REVERSE.id && idCardFront.selfieSrc != null && idCardBack.selfieSrc != null) {
                setBtnDisabled(false);
            }
        }
    }

    /**
     * Function for the "Next" button of the face image.
     */
    const validateBtnFace = () => {
        setBtnDisabled(true);
        if (facePhoto.selfieSrc != null) {
            setBtnDisabled(false);
        }
    }

    /**
     * Photo step control.
     */
    const btnDisabledFaceSteps = () => {
        if (activeStep == TAB_INDEX.TWO) {
            validateBtnFace()
        } else {
            setBtnDisabled(false);
        }
    }

    /**
     * Files step control.
     */
    const btnDisabledFilesSteps = () => {
        if (activeStep == TAB_INDEX.TWO) {
            validateBtnFiles();
        } else {
            setBtnDisabled(false);
        }
    }

    /**
     * Control of the step that includes INE/Passport and face photo.
     */
    const btnDisabledCompleteSteps = () => {
        if (activeStep == TAB_INDEX.TWO) {
            validateBtnFiles();
        } else if (activeStep == TAB_INDEX.FIVE) {
            validateBtnFace();
        } else {
            setBtnDisabled(false);
        }
    }

    /**
     * General function to upload files.
     * @param userId
     * @param fileType
     * @param side
     * @param description
     * @param photo
     */
    const createAndUpload = async (userId, fileType, side, description, photo) => {
        const model = {
            user: { id: userId },
            fileType: fileType,
            side: side,
            description: description
        };
        const { payload: { data: { data } } } = await dispatch(PublicCreateUserFile(model));
        if (data?.id) {
            const formData = new FormData();
            formData.append('id', data.id);
            formData.append('option', TABLES.USER_FILE);
            formData.append('file', getPhotoPicker(photo));
            await dispatch(PublicUploadImage(formData));
        }
    };

    /**
     * Function that saves card.
     */
    const handleSaveCardId = async () => {
        setLoading(true);
        await createAndUpload(userId, FILE_TYPE_MODEL.INE.id, FILE_TYPE_SIDE_MODEL.OBVERSE.id, documentType, idCardFront);
        await createAndUpload(userId, FILE_TYPE_MODEL.INE.id, FILE_TYPE_SIDE_MODEL.REVERSE.id, documentType, idCardBack);
        setLoading(false);
    };

    /**
     * Function that saves passport.
     */
    const handleSavePassport = async () => {
        setLoading(true);
        await createAndUpload(userId, FILE_TYPE_MODEL.PASSPORT.id, FILE_TYPE_SIDE_MODEL.NONE.id, documentType, passport);
        setLoading(false);
    }

    /**
     * Function to save face.
     */
    const handleSaveFaceValidation = async () => {
        setLoading(true);
        const body = {
            accessMethod: { id: accessMethodModel.FACE_RECOGNITION.id },
            user: { id: userId },
            description: 'Face recognition',
        };
        const { payload: { data: { data } } } = await dispatch(PublicCreateUserImage(body));
        if (data?.id) {
            let formData = new FormData();
            formData.append('id', data.id);
            formData.append('option', TABLES.USER_IMAGE);
            formData.append('file', getPhotoPicker(facePhoto));
            await dispatch(PublicUploadImage(formData));
            setLoading(false);
        }
    }

    /**
     * control of steps
     */
    const clickNextStepFiles = () => {
        if (type == FILE_TYPE_MODEL.PASSPORT.id) {
            if (passport.selfieSrc != null) {
                setActiveStep(TAB_INDEX.THREE)
            }
        } else {
            if (nextPhoto == FILE_TYPE_SIDE_MODEL.OBVERSE.id && idCardFront.selfieSrc != null) {
                setNextPhoto(FILE_TYPE_SIDE_MODEL.REVERSE.id);
                setReopenSuggestionShow(false);
                setStartup(true);
            } else if (nextPhoto == FILE_TYPE_SIDE_MODEL.REVERSE.id && idCardFront.selfieSrc != null && idCardBack.selfieSrc != null) {
                setActiveStep(TAB_INDEX.THREE)
            }
        }
    }

    /**
     * control next step by face
     */
    const clickNextStepFace = (step) => {
        if (facePhoto.selfieSrc != null) {
            setActiveStep(step)
        }
    }

    /**
     * control step general
     */
    const navigateStep = (step) => {
        setActiveStep(step);
    }

    /**
     * control for teh click event
     */
    const handleClickNext = () => {
        const completeSteps = {
            [TAB_INDEX.ONE]: () => navigateStep(TAB_INDEX.TWO),
            [TAB_INDEX.TWO]: () => clickNextStepFiles(),
            [TAB_INDEX.THREE]: () => navigateStep(TAB_INDEX.FOUR),
            [TAB_INDEX.FOUR]: () => navigateStep(TAB_INDEX.FIVE),
            [TAB_INDEX.FIVE]: () => clickNextStepFace(TAB_INDEX.SIX),
            [TAB_INDEX.SIX]: () => goCardsOptions()
        };

        const filesSteps = {
            [TAB_INDEX.ONE]: () => navigateStep(TAB_INDEX.TWO),
            [TAB_INDEX.TWO]: () => clickNextStepFiles(),
            [TAB_INDEX.THREE]: () => goCardsOptions()
        };

        const faceSteps = {
            [TAB_INDEX.ONE]: () => navigateStep(TAB_INDEX.TWO),
            [TAB_INDEX.TWO]: () => clickNextStepFace(TAB_INDEX.THREE),
            [TAB_INDEX.THREE]: () => goCardsOptions()
        };

        const stepsMap = steps.NAME == STEP_MODEL.FILES.NAME ? filesSteps : steps.NAME == STEP_MODEL.FACE.NAME ? faceSteps : completeSteps;
        const action = stepsMap[activeStep];

        if (action)
            action();
    };

    const goCardsOptions = () => {
        dispatch(GetInvitationById(id)).then(({payload:{data:{success}}})=>{
            if (success) navigate(getUrlParameters({ url: routes.invitationDetails.registrationProcess, id: id, guardFlowId }));
        });
    }

    const resetFieldsPhotoPicker = prev => ({
        ...prev,
        selfieSrc: null,
        fileSelected: null,
        srcSelected: null,
    });

    const resetCardId = () => {
        setNextPhoto(FILE_TYPE_SIDE_MODEL.OBVERSE.id);
        setIdCardFront(resetFieldsPhotoPicker);
        setIdCardBack(resetFieldsPhotoPicker);
    }

    const handleCancelStreamCardId = () => {
        if(idCardFront.selfieSrc != null){
            setShowModalCancelPicker(true);
        }else{
            resetCardId();
            setActiveStep(TAB_INDEX.ONE);
        }
    }

    const confirmModalPicker = () => {
        resetCardId();
        setShowModalCancelPicker(false);
        setActiveStep(TAB_INDEX.ONE);
    }

    const cancelModalPicker = () => {
        if(!startup){
            setStartup(true);
        }
        setShowModalCancelPicker(false);
    }

    const handleCancelStreamPassport = () => {
        setPassport(resetFieldsPhotoPicker);
        setActiveStep(TAB_INDEX.ONE)
    }

    const handleCancelStreamSelfie = () => {
        setFacePhoto(resetFieldsPhotoPicker);

        const isCompleteStep = steps?.NAME === STEP_MODEL.COMPLETE.NAME;
        const nextStep = isCompleteStep ? TAB_INDEX.FOUR : TAB_INDEX.ONE;

        setActiveStep(nextStep);
    };

    const handleNavigateBack = () => {
        const isComplete = steps.NAME === STEP_MODEL.COMPLETE.NAME && activeStep >= TAB_INDEX.THREE;
        const isFace = steps.NAME === STEP_MODEL.FACE.NAME;

        const url = (isComplete || isFace)
            ? routes.invitationDetails.registrationProcess
            : routes.invitationDetails.chooseValidationType;

        navigate(getUrlParameters({ url: url, id, guardFlowId }));
    };


    return {
        guardFlowId,
        type: parseInt(type),
        t, navigate,
        loading,
        btnDisabled,
        getInvitationById,

        steps, setSteps,
        activeStep, setActiveStep,

        passport, setPassport,

        idCardBack, setIdCardBack,

        idCardFront, setIdCardFront,

        facePhoto, setFacePhoto,

        nextPhoto, setNextPhoto,

        startup, setStartup,

        reopenSuggestionShow, setReopenSuggestionShow,

        handleClickNext,
        handleSavePassport,
        handleSaveCardId,
        handleSaveFaceValidation,

        handleCancelStreamCardId,
        handleCancelStreamPassport,
        handleCancelStreamSelfie,
        showModalCancelPicker,
        confirmModalPicker,
        cancelModalPicker,
        handleNavigateBack
    }

}

export default CreateValidationApp
